<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import MultiFormSelector from '../MultiFormSelector.vue';
import Select from 'primevue/select';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import ProgressSpinner from 'primevue/progressspinner';
import { ButtonType, CustomButton } from '@/components';
import { FormModel, UserModel, type IStudyGroupUser } from '@/models';
import { useFormStore } from '@/stores/forms';
import { useStudyGroupsStore } from '@/stores/studyGroups';
import StudyGroupUser from './StudyGroupUser.vue';
import { validateEmail } from '@/utils';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const formStore = useFormStore();
const groupStore = useStudyGroupsStore();

const onViewEntriesChangeListener = () => {
  groupStore.updatedGroup!.permissions.viewEntries =
    groupStore.selectedViewEntryPermission ===
    groupStore.viewEntriesPermissions[0];
};

const emits = defineEmits(['onClose']);

const closeDialog = (reloadGroups?: boolean) => {
  emits('onClose', reloadGroups);
};

const onViewFormsChangeListner = (updatedForms: FormModel[]) => {
  groupStore.updatedGroup!.permissions.forms = updatedForms.map(
    form => form.base64Id
  ) as string[];
};

const onEditFormsChangeListner = (updatedForms: FormModel[]) => {
  groupStore.updatedGroup!.permissions.editForms = updatedForms.map(
    form => form.base64Id
  ) as string[];
};

const isPristine = computed(() => {
  const initial = JSON.parse(JSON.stringify(groupStore.group!.permissions));
  const updated = JSON.parse(
    JSON.stringify(groupStore.updatedGroup!.permissions)
  );
  const sortedInitialForms = (initial.forms as string[]).sort();
  const sortedUpdatedForms = (updated.forms as string[]).sort();
  const sortedInitialEditForms = initial.editForms ? (initial.editForms as string[])?.sort() : [];
  const sortedUpdatedEditForms = updated.editForms ? (updated.editForms as string[])?.sort() : [];
  const pristine =
    groupStore.group!.name !== groupStore.updatedGroup!.name ||
    initial.viewEntries !== updated.viewEntries ||
    initial.export !== updated.export ||
    initial.manageInvest !== updated.manageInvest ||
    JSON.stringify(sortedInitialForms) !== JSON.stringify(sortedUpdatedForms) ||
    JSON.stringify(sortedInitialEditForms) !==
      JSON.stringify(sortedUpdatedEditForms);
  return pristine;
});

const copyLink = () => {
  navigator.clipboard.writeText(
    groupStore.updatedGroup?.inviteLink?.link ?? ''
  );
};

const activateDeactivateLink = () => {
  if (groupStore.groupLinkActivated) {
    groupStore.activateGroupLink(groupStore.group!.identifier);
  } else {
    groupStore.deactivateGroupLink(groupStore.group!.identifier);
  }
};

const resetLink = () => {
  groupStore.resetGroupLink(groupStore.group!.identifier);
};

const handleSaveGroupData = async () => {
  const updatedGroupData = JSON.parse(JSON.stringify(groupStore.updatedGroup!));
  if (
    (updatedGroupData.permissions.forms as string[]).length ===
    formStore.formList.length
  ) {
    updatedGroupData.permissions.forms = true;
  }
  if (
    (updatedGroupData.permissions.editForms as string[]).length ===
    formStore.formList.length
  ) {
    updatedGroupData.permissions.editForms = true;
  }
  if (groupStore.group!.create) {
    await groupStore.createStudyGroup(updatedGroupData);
  } else {
    await groupStore.updateStudyGroup(updatedGroupData);
  }
  closeDialog(true);
};

const handleResendInvite = (user: IStudyGroupUser) => {
  groupStore.reInviteUserToStudy(user);
};

const handleRemoveInvite = (user: IStudyGroupUser) => {
  if (user.status === 'Invited') {
    groupStore.deleteInvitedUserFromGroup(groupStore.group!.identifier, user);
  } else {
    groupStore.removeUserFromGroup(groupStore.group!.identifier, user);
  }
};

const isLoading = computed(
  () =>
    groupStore.isStudyGroupUpdating.isLoading ||
    groupStore.isStudyGroupsLoading.isLoading ||
    groupStore.isActivatingGroupLink.isLoading ||
    groupStore.isDeactivatingGroupLink.isLoading ||
    groupStore.isResettingGroupLink.isLoading ||
    groupStore.isReInvitingUser.isLoading ||
    groupStore.isDeletingInvitedUser.isLoading ||
    groupStore.isInvitingUser.isLoading ||
    groupStore.isDeletingGroup.isLoading ||
    groupStore.isStudyGroupCreating.isLoading
);

const loadingText = computed(() => {
  if (groupStore.isStudyGroupsLoading.isLoading) {
    return 'Fetching group details...';
  } else if (groupStore.isStudyGroupUpdating.isLoading) {
    return 'Updating group details';
  } else if (groupStore.isActivatingGroupLink.isLoading) {
    return 'Activating the group link';
  } else if (groupStore.isDeactivatingGroupLink.isLoading) {
    return 'Deactivating the group link';
  } else if (groupStore.isResettingGroupLink.isLoading) {
    return 'Resetting the group link';
  } else if (groupStore.isReInvitingUser.isLoading) {
    return 'Reinviting user to the group';
  } else if (groupStore.isDeletingInvitedUser.isLoading) {
    return 'Removing invited user from the group';
  } else if (groupStore.isInvitingUser.isLoading) {
    return 'Inviting user to the group';
  } else if (groupStore.isDeletingGroup.isLoading) {
    return 'Deleting the groupStore.group!...';
  } else if (groupStore.isStudyGroupCreating.isLoading) {
    return 'Creating a new groupStore.group!...';
  }
  return '';
});

const deleteGroup = async () => {
  await groupStore.deleteGroup(groupStore.group!);
  closeDialog(true);
};

// User invite dialog
const openUserInviteDialog = ref<boolean>(false);

const handleOpenCloseUserInviteDialog = () => {
  openUserInviteDialog.value = !openUserInviteDialog.value;
};

const inviteEmail = ref<string>();
const isUserInviteDialogPristine = computed(
  () => inviteEmail.value !== undefined && inviteEmail.value !== '' && !invalidInviteEmail.value
);

const inviteUser = () => {
  handleOpenCloseUserInviteDialog();
  const userInvite = UserModel.createEmpty([]);
  userInvite.email = inviteEmail.value!;
  groupStore.inviteUserToGroup([userInvite], groupStore.group!.identifier);
};

const invalidInviteEmail = computed(
  () => inviteEmail.value && !validateEmail(inviteEmail.value!)
);
</script>

<template>
  <div>
    <Dialog :visible="visible"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <template #container="{}">
        <div class="content-root">
          <div class="root">
            <div>
              <InputText type="text"
                class="input-title"
                v-model="groupStore.updatedGroup!.name"
                placeholder="Group name"/>
            </div>
            <div class="permissions">
              <h3 class="title">Group permissions</h3>

              <div class="link-section permission">
                <div>View forms</div>
                <MultiFormSelector :selectedFormsIds="groupStore.group!.permissions.forms as string[]"
                  @onFormChangeListener="onViewFormsChangeListner"/>
              </div>
              <div class="link-section permission">
                <div>Add entries</div>
                <MultiFormSelector :selectedFormsIds="groupStore.group!.permissions.editForms as string[]"
                  @onFormChangeListener="onEditFormsChangeListner"/>
              </div>
              <div class="link-section permission">
                <div>View entries</div>
                <Select v-model="groupStore.selectedViewEntryPermission"
                  @change="onViewEntriesChangeListener"
                  placeholder="Select"
                  :options="groupStore.viewEntriesPermissions"/>
              </div>
              <div class="link-section permission">
                <div>Edit cases</div>
                <ToggleSwitch v-model="groupStore.updatedGroup!.permissions.editCases"/>
              </div>
              <div class="link-section permission">
                <div>Allow data export</div>
                <ToggleSwitch v-model="groupStore.updatedGroup!.permissions.export"/>
              </div>
              <div class="link-section permission">
                <div>Allow study and users management</div>
                <ToggleSwitch v-model="groupStore.updatedGroup!.permissions.manageInvest"/>
              </div>
            </div>
            <div class="link" v-if="false">
              <div class="link-section link-toggle">
                <h3 class="title">Invite to study via link</h3>
                <ToggleSwitch v-model="groupStore.groupLinkActivated"
                  @change="activateDeactivateLink"/>
              </div>
              <div class="link-section copy-link"
                v-if="false">
                <InputGroup>
                  <InputText type="text"
                    disabled="true"
                    :value="groupStore.updatedGroup!.inviteLink?.link"/>
                  <InputGroupAddon>
                    <button v-ripple @click="copyLink">Copy link</button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
              <div class="link-description"
                v-if="groupStore.groupLinkActivated">
                Share this link to invite people to this study. You can
                <a class="reset" @click="resetLink">reset the link</a>
                to generate a new invite link.
              </div>
            </div>
            <div class="members" v-if="!groupStore.group!.create">
              <div class="members-header-row">
                <div class="memebers-header">
                  <h3 class="title">Members</h3>
                  <h3 class="member-count">
                    ({{
                      groupStore.updatedGroup!.activeUsers.length +
                      groupStore.updatedGroup!.pendingUsers.length
                    }})
                  </h3>
                </div>
                <div class="member-button">
                  <CustomButton text="Add member"
                    @onClick="handleOpenCloseUserInviteDialog"
                    :type="ButtonType.POSITIVE"/>
                </div>
              </div>
              <div class="member-list">
                <div v-for="user in groupStore.updatedGroup!.activeUsers"
                  :key="user.identifier"
                  class="member-list-item">
                  <StudyGroupUser :user="user"
                    @removeUser="handleRemoveInvite"/>
                </div>
                <div v-for="user in groupStore.updatedGroup!.pendingUsers"
                  :key="user.identifier"
                  class="member-list-item">
                  <StudyGroupUser :user="user"
                    @resendInvite="handleResendInvite"
                    @removeUser="handleRemoveInvite"/>
                </div>
              </div>
            </div>
          </div>
          <div class="footer">
            <div class="empty"></div>
            <div class="footer-buttons">
              <CustomButton text="Delete"
                @onClick="deleteGroup"
                v-if="!groupStore.group!.create"
                :type="ButtonType.NEGATIVE_OUTLINED"/>
              <CustomButton text="Cancel"
                @onClick="closeDialog"
                :margin-left="16"
                :type="ButtonType.OUTLINED"/>
              <CustomButton text="Save"
                :margin-left="16"
                :disabled="!isPristine"
                @onClick="handleSaveGroupData"
                :type="ButtonType.POSITIVE"/>
            </div>
          </div>
        </div>
        <div class="content-loading" v-if="isLoading">
          <ProgressSpinner strokeWidth="4" />
          <div>{{ loadingText }}</div>
        </div>
      </template>
    </Dialog>
    <Dialog :visible="openUserInviteDialog"
      modal
      class="dialog"
      append-to="self"
      @update:visible="handleOpenCloseUserInviteDialog">
      <template #container="{}">
        <div class="user-dialog-root">
          <div class="user-email">
            <h3 class="title">Email address</h3>
            <InputText type="text"
              class="user-email-input"
              v-model="inviteEmail"/>
            <div class="email-error">
              <span v-if="invalidInviteEmail" severity="error">Invalid email address</span>
            </div>
          </div>
        </div>
        <div class="user-dialog-buttons">
          <div class="empty"></div>
          <div class="user-dialog-buttons-right">
            <CustomButton text="Cancel"
              @onClick="handleOpenCloseUserInviteDialog"
              :type="ButtonType.OUTLINED"/>
            <CustomButton text="Invite to group"
              :margin-left="16"
              :disabled="!isUserInviteDialogPristine"
              @onClick="inviteUser"
              :type="ButtonType.POSITIVE"/>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../../assets/main.scss";
@import "../../../assets/dialog";
:deep(.p-dialog) {
  border-radius: 4px;
}
.content-root {
  overflow: auto;
}
.root {
  width: 600px;
  padding: 40px 40px 0 40px;
}
.content-loading {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.permissions {
  padding: 20px 0;
}
.title {
  font-weight: 700;
}
.input-title {
  font-weight: 700;
  font-size: 1.5rem;
  margin-bottom: 40px;
}
.link-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  padding-bottom: 8px;
}
.link {
  padding: 20px 0;
}
.copy-link {
  :deep(.p-inputtext) {
    font-size: 14px;
    cursor: text;
  }
  :deep(.p-inputgroupaddon) {
    padding: 0px;

    button {
      padding: 0.5rem 0.75rem;
      border: none;
      background: none;
      cursor: pointer;
    }

    &:hover {
      cursor: pointer;
      background-color: hsla(0, 0%, 0%, 0.15);
    }
  }
}
.link-description {
  font-size: 14px;
  font-weight: 400;

  .reset {
    color: $primary-color;
    cursor: pointer;
    &:hover {
      background-color: hsla(0, 0%, 0%, 0.15);
    }
  }
}
.members {
  padding: 20px 0;
}
.members-header-row {
  display: flex;
  justify-content: space-between;
}
.memebers-header {
  display: flex;

  .member-count {
    margin-left: 4px;
  }
}
.member-list-item {
  padding: 8px 0;
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;

  .footer-buttons {
    display: flex;
  }
}
.user-dialog-root {
  width: 600px;
  padding: 40px 40px 0 40px;
}
.user-email-input {
  width: 100%;
  margin-top: 8px;
}
.email-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
.user-dialog-buttons {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  margin-top: 40px;
}
.user-dialog-buttons-right {
  display: flex;
}
</style>
